/**
 * TTS audio providers — currently ElevenLabs (proxied) only.
 *
 * The pre-Phase-2 single-article.js carried two extra providers (Azure
 * direct + browser SpeechSynthesis) for fallback. Both were dead-code:
 *   - tryAzure was never wired into the Listen click handler.
 *   - useBrowserVoice activated only when ttsMode = true, but that flag
 *     was set ONLY by useBrowserVoice itself — circularly unreachable.
 *
 * The browser-voice fallback was deliberately retired (see comment in
 * the historical handler: "ElevenLabs only — no browser-TTS fallback…
 * No robotic auto-speak."). The Azure path was speculative and never
 * shipped. Both are removed here; this module exposes a single canonical
 * promise-based provider.
 *
 * Build path:
 *   src   assets/js/src/single-article/audio-providers.ts
 *   ↓ esbuild (IIFE, target ES2020)
 *   dist  assets/dist/js/single-article/audio-providers.js  (attaches window.XOT.article.audio)
 *
 * @since Phase 2 — JS modernization, bite 8
 */

const DEFAULT_ENDPOINT  = '/wp-json/xot/v1/tts';
const MAX_TEXT_CHARS    = 2500;   // ElevenLabs hard limit; longer texts get truncated server-side anyway
const DEFAULT_VOICE_PREF = 'british';

export interface ElevenLabsOptions {
    /** Server endpoint that proxies to ElevenLabs (keeps the API key off the client). */
    endpoint?: string;
    /** Voice preference passed to the proxy — 'british' or 'american'. */
    voicePref?: string;
    /** Article post ID; lets the proxy persist the resolved audio URL as postmeta for JSON-LD. */
    postId?: number;
}

/** Result for the cancellation-safe TTS request. */
export interface ElevenLabsResult {
    /** Object-URL pointing at the synthesized audio Blob. Caller must
        `URL.revokeObjectURL` when done. */
    url: string;
    /** Status echoed from the proxy (for logging). */
    status: number;
}

/**
 * Request ElevenLabs TTS synthesis. Returns a Promise of an object URL
 * pointing to the audio Blob.
 *
 * The text is truncated to {@link MAX_TEXT_CHARS} client-side. The proxy
 * truncates further if needed, so this is just defensive.
 *
 * Rejects with the HTTP status code on non-2xx response (the legacy
 * caller used to log via console.warn — that's now the caller's choice).
 */
export function requestElevenLabsTTS(text: string, opts: ElevenLabsOptions = {}): Promise<ElevenLabsResult> {
    const endpoint  = opts.endpoint  || DEFAULT_ENDPOINT;
    const voicePref = opts.voicePref || DEFAULT_VOICE_PREF;
    const postId    = opts.postId    ?? 0;

    return fetch(endpoint, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
            text:       text.substring(0, MAX_TEXT_CHARS),
            voice_pref: voicePref,
            post_id:    postId,
        }),
    }).then(async (r) => {
        if (!r.ok) {
            // Attempt to surface the server's error payload — but never crash on parse failure.
            try {
                const err = await r.json();
                console.warn('[xot-tts]', r.status, err?.error, '—', err?.message);
            } catch {
                // empty / non-JSON response — status code is enough
            }
            // eslint-disable-next-line no-throw-literal
            throw r.status;
        }
        return r.blob();
    }).then((blob) => ({ url: URL.createObjectURL(blob), status: 200 }));
}

/* ── window.XOT.article.audio attachment (legacy IIFE consumers) ───── */
interface XotArticleAudio {
    requestElevenLabsTTS: typeof requestElevenLabsTTS;
}

declare global {
    interface Window {
        XOT?: {
            article?: {
                text?:      unknown;
                highlight?: unknown;
                progress?:  unknown;
                share?:     unknown;
                audio?:     XotArticleAudio;
            };
            [k: string]: unknown;
        };
    }
}

if (typeof window !== 'undefined') {
    const xot = (window.XOT ?? {}) as NonNullable<Window['XOT']>;
    const article = (xot.article ?? {}) as NonNullable<NonNullable<Window['XOT']>['article']>;
    article.audio = { requestElevenLabsTTS };
    xot.article = article;
    window.XOT  = xot;
}
